import React from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  FormHelperText,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  DatePicker as MuiDatePicker,
  TimePicker as MuiTimePicker,
  DateTimePicker as MuiDateTimePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  CalendarToday,
  AccessTime,
  DateRange,
  Clear,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledFormControl = styled(FormControl)<{ variant?: 'default' | 'outlined' | 'filled' }>(
  ({ theme, variant = 'default' }) => ({
    width: '100%',
    '& .MuiInputBase-root': {
      ...(variant === 'outlined' && {
        borderRadius: `calc(${theme.shape.borderRadius}px * 1.5)`,
      }),
      ...(variant === 'filled' && {
        backgroundColor: theme.palette.grey[100],
        '&:hover': {
          backgroundColor: theme.palette.grey[200],
        },
        '&.Mui-focused': {
          backgroundColor: theme.palette.background.paper,
        },
      }),
    },
  })
);

export interface BaseDatePickerProps {
  /**
   * The selected date value
   */
  value?: Date | null;
  /**
   * Callback fired when the value changes
   */
  onChange?: (date: Date | null) => void;
  /**
   * The label content
   */
  label?: string;
  /**
   * Helper text to display below the input
   */
  helperText?: string;
  /**
   * If true, the input will indicate an error
   */
  error?: boolean;
  /**
   * If true, the input is required
   */
  required?: boolean;
  /**
   * If true, the input is disabled
   */
  disabled?: boolean;
  /**
   * If true, the input is read-only
   */
  readOnly?: boolean;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Visual variant of the picker
   */
  variant?: 'default' | 'outlined' | 'filled';
  /**
   * Size of the component
   */
  size?: 'small' | 'medium';
  /**
   * Minimum selectable date
   */
  minDate?: Date;
  /**
   * Maximum selectable date
   */
  maxDate?: Date;
  /**
   * Date format to display
   */
  format?: string;
  /**
   * Whether to show clear button
   */
  showClearButton?: boolean;
  /**
   * Custom validation function
   */
  validate?: (date: Date | null) => string | null;
  /**
   * Whether to open picker on focus
   */
  openOnFocus?: boolean;
  /**
   * Position of the label
   */
  labelPosition?: 'top' | 'left' | 'floating';
}

export interface DatePickerProps extends BaseDatePickerProps {
  /**
   * Type of date picker
   */
  type?: 'date';
  /**
   * Views to show in the picker
   */
  views?: ('year' | 'month' | 'day')[];
  /**
   * Starting view when opening the picker
   */
  openTo?: 'year' | 'month' | 'day';
  /**
   * Whether to show days from other months
   */
  showDaysOutsideCurrentMonth?: boolean;
}

export interface TimePickerProps extends BaseDatePickerProps {
  /**
   * Type of time picker
   */
  type: 'time';
  /**
   * Views to show in the picker
   */
  views?: ('hours' | 'minutes' | 'seconds')[];
  /**
   * Time step in minutes
   */
  minutesStep?: number;
  /**
   * Whether to use 12-hour format
   */
  ampm?: boolean;
}

export interface DateTimePickerProps extends BaseDatePickerProps {
  /**
   * Type of datetime picker
   */
  type: 'datetime';
  /**
   * Views to show in the picker
   */
  views?: ('year' | 'month' | 'day' | 'hours' | 'minutes')[];
  /**
   * Starting view when opening the picker
   */
  openTo?: 'year' | 'month' | 'day' | 'hours' | 'minutes';
  /**
   * Time step in minutes
   */
  minutesStep?: number;
  /**
   * Whether to use 12-hour format
   */
  ampm?: boolean;
}

const renderClearButton = (
  value: Date | null,
  onChange: (date: Date | null) => void,
  disabled?: boolean
) => {
  if (!value || disabled) return null;
  
  return (
    <InputAdornment position="end">
      <IconButton
        size="small"
        onClick={() => onChange(null)}
        edge="end"
      >
        <Clear fontSize="small" />
      </IconButton>
    </InputAdornment>
  );
};

export const DatePicker: React.FC<DatePickerProps | TimePickerProps | DateTimePickerProps> = ({
  type = 'date',
  value,
  onChange,
  label,
  helperText,
  error = false,
  required = false,
  disabled = false,
  readOnly = false,
  placeholder,
  variant = 'default',
  size = 'medium',
  minDate,
  maxDate,
  format,
  showClearButton = false,
  validate,
  labelPosition = 'floating',
  ...props
}) => {
  const [validationError, setValidationError] = React.useState<string | null>(null);

  const handleChange = (newValue: Date | null | undefined) => {
    const dateValue = newValue || null;
    const validationResult = validate ? validate(dateValue) : null;
    setValidationError(validationResult);
    onChange?.(dateValue);
  };

  const hasError = error || !!validationError;
  const displayHelperText = validationError || helperText;

  const getTextFieldVariant = () => {
    if (variant === 'default') return 'outlined' as const;
    return variant;
  };

  const commonProps = {
    value: value || null,
    onChange: handleChange,
    disabled,
    readOnly,
    minDate,
    maxDate,
    format,
  };

  const renderPicker = () => {
    switch (type) {
      case 'time': {
        const timeProps = props as TimePickerProps;
        return (
          <MuiTimePicker
            {...commonProps}
            views={timeProps.views}
            minutesStep={timeProps.minutesStep}
            ampm={timeProps.ampm}
            slotProps={{
              textField: {
                variant: getTextFieldVariant(),
                size,
                error: hasError,
                required,
                placeholder,
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTime color="action" />
                    </InputAdornment>
                  ),
                  ...(showClearButton
                    ? {
                        endAdornment: renderClearButton(
                          value || null,
                          handleChange,
                          disabled
                        ),
                      }
                    : {}),
                },
              },
            }}
          />
        );
      }

      case 'datetime': {
        const dateTimeProps = props as DateTimePickerProps;
        return (
          <MuiDateTimePicker
            {...commonProps}
            views={dateTimeProps.views}
            openTo={dateTimeProps.openTo}
            minutesStep={dateTimeProps.minutesStep}
            ampm={dateTimeProps.ampm}
            slotProps={{
              textField: {
                variant: getTextFieldVariant(),
                size,
                error: hasError,
                required,
                placeholder,
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateRange color="action" />
                    </InputAdornment>
                  ),
                  ...(showClearButton
                    ? {
                        endAdornment: renderClearButton(
                          value || null,
                          handleChange,
                          disabled
                        ),
                      }
                    : {}),
                },
              },
            }}
          />
        );
      }

      default: {
        const dateProps = props as DatePickerProps;
        return (
          <MuiDatePicker
            {...commonProps}
            views={dateProps.views}
            openTo={dateProps.openTo}
            showDaysOutsideCurrentMonth={dateProps.showDaysOutsideCurrentMonth}
            slotProps={{
              textField: {
                variant: getTextFieldVariant(),
                size,
                error: hasError,
                required,
                placeholder,
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday color="action" />
                    </InputAdornment>
                  ),
                  ...(showClearButton
                    ? {
                        endAdornment: renderClearButton(
                          value || null,
                          handleChange,
                          disabled
                        ),
                      }
                    : {}),
                },
              },
            }}
          />
        );
      }
    }
  };

  if (labelPosition === 'top' || labelPosition === 'left') {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <StyledFormControl variant={variant === 'default' ? undefined : variant} error={hasError}>
          {label && (
            <FormLabel
              required={required}
              sx={{
                mb: labelPosition === 'top' ? 1 : 0,
                mr: labelPosition === 'left' ? 2 : 0,
                minWidth: labelPosition === 'left' ? 120 : 'auto',
              }}
            >
              {label}
            </FormLabel>
          )}
          <Box sx={{ display: labelPosition === 'left' ? 'flex' : 'block', alignItems: 'center' }}>
            {renderPicker()}
          </Box>
          {displayHelperText && (
            <FormHelperText error={hasError}>{displayHelperText}</FormHelperText>
          )}
        </StyledFormControl>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledFormControl variant={variant === 'default' ? undefined : variant} error={hasError}>
        <Box sx={{ position: 'relative' }}>
          {renderPicker()}
          {label && labelPosition === 'floating' && (
            <FormLabel
              required={required}
              sx={{
                position: 'absolute',
                top: -8,
                left: 14,
                fontSize: '0.75rem',
                backgroundColor: 'background.paper',
                px: 0.5,
                zIndex: 1,
              }}
            >
              {label}
            </FormLabel>
          )}
        </Box>
        {displayHelperText && (
          <FormHelperText error={hasError}>{displayHelperText}</FormHelperText>
        )}
      </StyledFormControl>
    </LocalizationProvider>
  );
}; 